import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { TIMELINE_CONST } from '../../common/constant/common-constants';
import {
  formatDocumentLabel,
  filterMachinesBySerialQuery,
  getAssignmentDate,
  getAssignmentModel,
  getAssignmentSerial,
  getAssignmentVersion,
  getMachineShortSerial,
} from '../../helper/DeviceDocumentsHelper';

const formatAssignedAt = (value) => {
  if (!value) return '—';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format(TIMELINE_CONST.DATE_TIME_FORMAT) : value;
};

const MachineAssignmentsPanel = ({
  assignments,
  loadingAssignments,
  onAssignClick,
  onChangeClick,
  onUnassign,
}) => {
  const [tableQuery, setTableQuery] = useState('');

  const filteredAssignments = useMemo(() => {
    const q = tableQuery.trim();
    if (!q) return assignments;
    const lower = q.toLowerCase();
    return assignments.filter((row) => {
      const serialMatch = filterMachinesBySerialQuery(
        [{ machineUDI: getAssignmentSerial(row) }],
        q
      ).length > 0;
      if (serialMatch) return true;
      return String(getAssignmentModel(row)).toLowerCase().includes(lower)
        || String(getAssignmentVersion(row)).toLowerCase().includes(lower)
        || String(row.document?.title || '').toLowerCase().includes(lower);
    });
  }, [assignments, tableQuery]);

  return (
    <>
      <div className="docs-filters-bar">
        <input
          className="docs-search-input"
          value={tableQuery}
          onChange={(event) => setTableQuery(event.target.value)}
          placeholder="Search last 4 digits, machine number, or version…"
          aria-label="Search mapped machines"
        />
        <button type="button" className="docs-btn docs-btn--primary" onClick={onAssignClick}>
          Assign version
        </button>
      </div>

      <div className="docs-toolbar">
        <h2 className="docs-section-title">Machine document version</h2>
      </div>

      {loadingAssignments && assignments.length === 0 ? (
        <div className="docs-empty">Loading mapped machines…</div>
      ) : filteredAssignments.length === 0 ? (
        <div className="docs-empty">
          No document version is assigned to a machine yet. Assign a version to a machine number.
        </div>
      ) : (
        <div className="docs-table-wrap">
          <table className="docs-table">
            <thead>
              <tr>
                <th>Machine number</th>
                <th>Model</th>
                <th>Document version</th>
                <th>Assigned date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredAssignments.map((row) => {
                const serial = getAssignmentSerial(row);
                const version = getAssignmentVersion(row);
                return (
                  <tr key={`${getAssignmentModel(row)}-${row.machineUDIId}-${row.documentId}-${serial}`}>
                    <td>
                      <span className="docs-cell-title">
                        {serial ? getMachineShortSerial(serial) : `ID ${row.machineUDIId || '—'}`}
                      </span>
                      {serial ? <span className="docs-cell-sub">{serial}</span> : null}
                    </td>
                    <td>{getAssignmentModel(row) || '—'}</td>
                    <td>{version || formatDocumentLabel(row.document) || '—'}</td>
                    <td>{formatAssignedAt(getAssignmentDate(row))}</td>
                    <td>
                      <div className="docs-actions">
                        {serial ? (
                          <>
                            <button
                              type="button"
                              className="docs-btn docs-btn--ghost docs-btn--small"
                              onClick={() => onChangeClick(row)}
                            >
                              Change
                            </button>
                            <button
                              type="button"
                              className="docs-btn docs-btn--danger docs-btn--small"
                              onClick={() => onUnassign(serial)}
                            >
                              Remove mapping
                            </button>
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default MachineAssignmentsPanel;
